export enum ExecutionStatus {
  pending = 'pending',
  started = 'started',
  finished = 'finished',
  failed = 'failed',
  cancelledRunning = 'cancelledRunning',
  cancelledPending = 'cancelledPending',
  error = 'error',
}

/**
 * The statuses at which a job execution is over and no further updates are
 * expected. Anything not in this list is still in flight.
 *
 * Kept next to the enum so the several places that need "is this the end?" —
 * Firestore finalisation, the terminal-notification dispatch, the reconciler —
 * cannot drift apart from each other.
 */
export const TERMINAL_EXECUTION_STATUSES: readonly ExecutionStatus[] = [
  ExecutionStatus.finished,
  ExecutionStatus.failed,
  ExecutionStatus.error,
  ExecutionStatus.cancelledRunning,
  ExecutionStatus.cancelledPending,
];

export function isTerminalExecutionStatus(
  status: ExecutionStatus
): boolean {
  return TERMINAL_EXECUTION_STATUSES.includes(status);
}

/**
 * How a terminal state was established.
 *
 * `workflow` means the workflow told us. `reconciler` means it did not, and we
 * asked Google what happened to the execution instead — so the job ran, but the
 * step-level detail may be missing or stale. Anything wanting to explain *why* a
 * run failed should check this before trusting `jobStepStatuses`.
 *
 * Lives here, beside the statuses it qualifies, because it crosses the same
 * boundaries they do: the SQL entity persists it, jobs-api classifies on it, and
 * the notification payload carries it. It was previously written out inline in
 * five places.
 */
export type TerminalDetectedBy = 'workflow' | 'reconciler';

/**
 * Google's live view of where a Cloud Workflows execution's pointer is — routine
 * and step names from the workflow YAML. Only obtainable while an execution is
 * ACTIVE, and NOT persisted: this is transient engine detail, not job state.
 * For "how did each phase of the job go", see `jobStepStatuses`.
 */
export interface CurrentStep {
  routine?: string | null;
  step?: string | null;
}

export interface WorkflowExecutionStatus {
  jobId: number;
  customerId: number;
  executionStatus: ExecutionStatus;
  dt: Date;
  executionId?: string;
  errorMessage?: string;
  taskId?: string;
  /**
   * Step outcomes as reported by the workflow, keyed by step name. On a terminal
   * row this is the frozen end state — the durable copy of what Firestore holds
   * during the run and deletes when the job completes. Null on pending/started
   * rows, which predate the workflow reporting anything.
   */
  jobStepStatuses?: Record<string, JobStepStatus>;
  /**
   * Dispatch outbox marker, terminal rows only. Set through a conditional update
   * (`WHERE notified_at IS NULL`) so exactly one caller wins and sends the
   * callback. A terminal row where this is still null is an outstanding dispatch
   * for the reconciler to retry.
   */
  notifiedAt?: Date;
  /**
   * Who established this terminal state. Stored rather than re-derived so a
   * dispatch retry keeps the original provenance — see JobsStatusEntity.
   */
  detectedBy?: TerminalDetectedBy;
  deletedAt?: Date;
}

/**
 * Job step status values used within workflow execution.
 *
 * These arrive as untyped strings across the YAML → JSON boundary, so nothing
 * type-checks the workflows against this union — keep it matching what the
 * workflow YAMLs actually write. EDUS-2845 found three ways it had drifted:
 * `'not-started'` was declared and written nowhere, while `'pending'` and
 * `'completed_with_errors'` were written and declared nowhere.
 *
 * `'completed_with_errors'` is the step-level equivalent of a partial run: the
 * step finished but not everything in it worked.
 */
export type JobStepStatus =
  | 'completed'
  | 'completed_with_errors'
  | 'in-progress'
  | 'pending'
  | 'error';

/**
 * The outcome buckets a job can be notified about when it reaches a terminal
 * state. `partial` is a finished run in which at least one step did not
 * complete — the data is largely there but something inside it failed.
 */
export const TERMINAL_OUTCOMES = [
  'success',
  'partial',
  'failure',
  'cancel',
] as const;

/** Derived from the array above so a bucket can't be added to only one. */
export type TerminalOutcome = (typeof TERMINAL_OUTCOMES)[number];

/**
 * Every terminal outcome that is not a clean success — what an initial sync
 * records as its last outcome when it has to be retried.
 */
export type InitialSyncFailureOutcome = Exclude<TerminalOutcome, 'success'>;

/**
 * The named callback targets a job may ask to be notified at. Each key is
 * resolved to a URL and OIDC audience from jobs-api config — see
 * {@link TerminalNotifications} for why it is never a URL.
 */
export const NOTIFICATION_TARGET_KEYS = ['onboarding-sync'] as const;

export type NotificationTargetKey = (typeof NOTIFICATION_TARGET_KEYS)[number];

/**
 * Per-job request to be called back when the job reaches a terminal state.
 *
 * Each value is a {@link NotificationTargetKey} — a NAMED KEY resolved to a URL
 * and OIDC audience from jobs-api config — never a URL. The enqueue path is reachable from a browser (the app
 * header's manual cache refresh posts to the same asset-service-layer endpoint
 * as the onboarding kickoff), so accepting a URL here would let a signed-in user
 * choose what a backend service calls.
 *
 * Every bucket is optional and an absent bucket means NO CALL — there is
 * deliberately no fallback between buckets, including partial → success. A
 * consumer that wants to hear about partial runs must say so; jobs-api logs when
 * it classifies a run partial and finds no endpoint configured, so the silence
 * is diagnosable.
 */
export type TerminalNotifications = Partial<
  Record<TerminalOutcome, NotificationTargetKey>
>;

/**
 * Keys for job counter fields that can be atomically incremented
 * Extend this union type to add new counter fields
 */
export type JobCounterKey =
  | 'totalDeviceAssetsWritten'
  | 'totalGroupingsAssetsWritten'
  | 'totalUsersAssetsWritten';

/**
 * Valid job type identifiers for differentiating job sources
 */
export type JobType =
  | 'bulk-edit' // Bulk edit device information
  | 'single-device-edit' // Edit single device from device details view
  | 'in-cell-edit' // Quick edit in grid cell
  | 'cache-refresh' // Manual refresh cache (all devices)
  | 'refresh-selected' // Refresh selected devices from GAC
  | 'move-ou' // Move devices to specific OU
  | 'move-last-user-ou' // Move devices to last user's OU
  | 'disable' // Disable devices
  | 'enable' // Enable devices
  | 'wipe-users' // Profile/user wipe
  | 'factory-reset' // Factory reset/powerwash
  | 'deprovision' // Deprovision devices
  | 'export' // Export data to file
  | 'import-validate' // Validate import file
  | 'import-execute'; // Execute import updates

/**
 * Job type constants for categorizing jobs in the jobs panel
 * These values are stored in additionalJobData.jobType and used for display
 */
export const JobTypes = {
  BULK_EDIT: 'bulk-edit',
  SINGLE_DEVICE_EDIT: 'single-device-edit',
  IN_CELL_EDIT: 'in-cell-edit',
  CACHE_REFRESH: 'cache-refresh',
  REFRESH_SELECTED: 'refresh-selected',
  MOVE_OU: 'move-ou',
  MOVE_LAST_USER_OU: 'move-last-user-ou',
  DISABLE: 'disable',
  ENABLE: 'enable',
  WIPE_USERS: 'wipe-users',
  FACTORY_RESET: 'factory-reset',
  DEPROVISION: 'deprovision',
  EXPORT: 'export',
  IMPORT_VALIDATE: 'import-validate',
  IMPORT_EXECUTE: 'import-execute',
} as const;

/**
 * Display names for job types
 * Maps jobType values to user-friendly display strings
 */
export const JOB_TYPE_DISPLAY_NAMES: Record<JobType, string> = {
  [JobTypes.BULK_EDIT]: 'Edit device(s) info',
  [JobTypes.SINGLE_DEVICE_EDIT]: 'Edit device info',
  [JobTypes.IN_CELL_EDIT]: 'Cell Edits',
  [JobTypes.CACHE_REFRESH]: 'Refresh Cache',
  [JobTypes.REFRESH_SELECTED]: 'Refresh Selected Device(s)',
  [JobTypes.MOVE_OU]: 'Device(s) Move OU',
  [JobTypes.MOVE_LAST_USER_OU]: "Move device(s) to last user's OU",
  [JobTypes.DISABLE]: 'Disable Device(s)',
  [JobTypes.ENABLE]: 'Enable Device(s)',
  [JobTypes.WIPE_USERS]: 'Profile wipe device(s)',
  [JobTypes.FACTORY_RESET]: 'Factory reset device(s)',
  [JobTypes.DEPROVISION]: 'Deprovision device(s)',
  [JobTypes.EXPORT]: 'Export Data',
  [JobTypes.IMPORT_VALIDATE]: 'Import Validation',
  [JobTypes.IMPORT_EXECUTE]: 'Import Data',
} as const;

/**
 * Per-job-type rendering policy for fields shown in the Job Notification
 * Panel detail rows. Job types not listed in JOB_TYPE_FIELD_POLICY use only
 * the global defaults (see HIDDEN_ADDITIONAL_DATA_FIELDS in
 * job-card.component.ts).
 *
 * Add an entry to JOB_TYPE_FIELD_POLICY when a specific job type has fields
 * that shouldn't be shown to the user.
 *
 * For fields that need *custom rendering* (not just hiding), use the same
 * pattern as outputReference: hide it here, then render it as a dedicated
 * piece of UI in job-card.component.html.
 */
export interface JobTypeFieldPolicy {
  /**
   * Field names in additionalJobData to hide from the generic detail rows
   * for this job type. Additive to the global HIDDEN_ADDITIONAL_DATA_FIELDS.
   */
  readonly hideFields?: readonly string[];
}

/**
 * Per-job-type field-rendering policy. Only job types that need overrides
 * appear here — all others render with global defaults.
 */
export const JOB_TYPE_FIELD_POLICY: Partial<
  Record<JobType, JobTypeFieldPolicy>
> = {
  [JobTypes.IMPORT_VALIDATE]: {
    // errorCategories is a nested object used by the import dialog's
    // validation report screen. It would render as "[object Object]" in the
    // generic detail rows and isn't meaningful to the user in the jobs panel.
    hideFields: ['errorCategories'],
  },
};

/**
 * Type-safe container for atomic job counters
 * Values are always numbers — used with Firestore FieldValue.increment()
 */
export type JobCounters = Partial<Record<JobCounterKey, number>>;

/**
 * Template literal type for referencing jobCounters fields
 * Used by DTOs that need to specify which counter field to increment
 */
export type JobCounterField = `jobCounters.${JobCounterKey}`;

/**
 * Extensible container for arbitrary job-specific data
 * Used for one-time result data like filePath, outputUrl, etc.
 */
export type AdditionalJobData = Record<string, unknown>;

/**
 * Typed shape for the additionalJobData fields written by bulk-action workflows
 * (device-assets-update-service-workflow and friends). Read by the SPOG client's
 * stuck-job failsafe to scope the selection lock per grid-session-uuid and to
 * scale the threshold per selected-asset-count.
 *
 * The fields are written by saas-device-api when triggering a bulk-action
 * workflow (selectedCount = materialized update list length; gridSessionUuid =
 * X_CDW_GRID_SESSION header value). They are NOT separate top-level fields on
 * the Firestore execution doc — they are nested inside additionalJobData per
 * the existing extensibility contract.
 *
 * The threshold formula reads selectedCount; the lock-active predicate reads
 * gridSessionUuid to filter "is this MY tab's job?" since each browser tab has
 * its own grid-session UUID (sessionStorage-scoped per cdw-ssrm-grid).
 */
export interface BulkActionAdditionalJobData {
  /** Number of assets the bulk-action job is operating on; used for failsafe threshold scaling */
  selectedCount?: number;
  /** Originating grid-session UUID for per-tab lock scoping */
  gridSessionUuid?: string;
}

/**
 * Typed read helper for extracting bulk-action fields from additionalJobData.
 * Returns undefined-safe view of the typed shape; cast-free at call sites.
 */
export function getBulkActionJobData(
  data: AdditionalJobData | undefined
): BulkActionAdditionalJobData {
  if (!data) return {};
  const typed = data as Partial<BulkActionAdditionalJobData>;
  const selectedCount =
    typeof typed.selectedCount === 'number' ? typed.selectedCount : undefined;
  const gridSessionUuid =
    typeof typed.gridSessionUuid === 'string'
      ? typed.gridSessionUuid
      : undefined;
  return {
    ...(selectedCount !== undefined ? { selectedCount } : {}),
    ...(gridSessionUuid !== undefined ? { gridSessionUuid } : {}),
  };
}

/**
 * Job execution status model for Firestore
 * Represents the document structure at: /customers/{customerId}/jobs/{jobId}/executions/{executionId}
 *
 * Based on WorkflowExecutionStatus with additional fields:
 * - id: maps to jobId for IdModel compatibility
 * - customerId: required for collection group queries and repository pattern
 * - workflowName: required for filtering executions by workflow type
 * - jobCounters: atomic counters for tracking progress (e.g., totalAssetsWritten for cache refresh jobs)
 * - additionalJobData: extensible container for arbitrary job-specific data (e.g., filePath for exports)
 * - createdByUserId: Database user ID (integer FK to users table) of the user who created the job
 * - createdByUserEmail: Email of the user who created the job (for display)
 * - createdAt: Timestamp when the job was created (from SQL createdAt column)
 *
 * Omits: deletedAt, taskId, notifiedAt, detectedBy (SQL-side dispatch
 * bookkeeping — the Firestore document is deleted at terminal, so it never
 * outlives a dispatch).
 *
 * `jobStepStatuses` is inherited rather than redeclared: it used to be called
 * `jobSteps` here and existed only on this Firestore type while the SQL type
 * carried a dead `currentSteps`. EDUS-2845 unified them.
 */
export interface JobExecutionStatus
  extends Omit<
    WorkflowExecutionStatus,
    'deletedAt' | 'taskId' | 'notifiedAt' | 'detectedBy'
  > {
  id: number;
  /** Customer ID - numeric, validated by @CdwCustomerId() decorator on all write paths */
  customerId: number;
  workflowName: string;
  /** Atomic counters for tracking progress */
  jobCounters?: JobCounters;
  /** Extensible container for arbitrary job-specific data */
  additionalJobData?: AdditionalJobData;
  /** Client-generated UUID for tracking jobs across browser restarts */
  correlationId?: string;
  /** Database user ID (integer FK to users table) of the user who created the job */
  createdByUserId?: number;
  /** Email of the user who created the job (for display purposes) */
  createdByUserEmail?: string;
  /** Timestamp when the job was created (matches SQL createdAt) */
  createdAt?: Date;
}
